import {TouchEvent, useRef, useState} from "react";
import {faChevronLeft, faChevronRight, faArrowUpRightFromSquare} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTranslation} from "react-i18next";

type Button = {
    text: string,
    href: string,
    color?: string,
    pulse?: boolean
}

export type Item = {
    name: string,
    background: string
    buttons: Button[]
    captions: string[]
    stackImgs: string[]
}

type CarrouselItemProps = {
    item: Item
}

const CarrouselItem = (props: CarrouselItemProps) => {
    const ButtonG = (props: { button: Button }) => {
        return (
            <button
                onClick={() => window.open(props.button.href, '_blank')}
                className={"inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold " +
                    "text-white transition-colors hover:bg-accentHover active:bg-accentHover"}>
                {props.button.text}
                <FontAwesomeIcon size={"sm"} icon={faArrowUpRightFromSquare}/>
            </button>
        )
    }
    return (
        <div className={"relative flex w-full flex-1 flex-col"}>
            <div className={"absolute inset-0"}>
                <img className={"w-full h-full object-cover object-center"} alt="" src={props.item.background}/>
                <div className={"absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/55 to-slate-950/25"}/>
            </div>
            <div className={"relative flex flex-1 flex-col justify-between gap-4 p-4 pb-16 sm:p-6 sm:pb-14"}>
                <h3 className={"text-2xl font-bold text-white drop-shadow sm:text-3xl"}>{props.item.name}</h3>
                <div className={"flex flex-1 items-center"}>
                    <div className={"max-w-xl rounded-xl border border-white/10 bg-slate-950/50 p-4 " +
                        "text-sm text-white/90 backdrop-blur-sm sm:p-5 sm:text-base"}>
                        <ul className={"space-y-1.5"}>
                            {props.item.captions.map((x, i) =>
                                <li key={i} className={"flex gap-2"}>
                                    <span className={"text-blue-300"}>•</span>
                                    <span>{x}</span>
                                </li>
                            )}
                            <li className={"flex flex-wrap items-center gap-1.5 pt-2"}>
                                <span className={"mr-1 font-semibold text-white/70"}>Stack:</span>
                                {props.item.stackImgs.map((x, i) =>
                                    <img className={"h-7 w-7 inline object-contain"}
                                         alt={x.substring(x.lastIndexOf("/") + 1, x.search("\\."))}
                                         key={i} src={`${x}`}/>)}
                            </li>
                        </ul>
                    </div>
                </div>
                <div className={"flex flex-wrap justify-end gap-2"}>
                    {props.item.buttons.map((x, i) => <ButtonG button={x} key={i}/>)}
                </div>
            </div>
        </div>
    )
}

const Carrousel = () => {
    const {t} = useTranslation();
    const kanbanItem: Item = {
        name: t("projects.kanban.name"),
        background: "/img/projects/bannerKan.png",
        captions: [t("projects.kanban.caption1"), t("projects.kanban.caption2"), t("projects.kanban.caption3")],
        stackImgs: [
            "/img/stack/react.png",
            "/img/stack/tailwind.png",
            "/img/stack/go.png",
            "/img/stack/yjs.png",
            "/img/stack/mongo.png",
            "/img/stack/docker.png",
            "/img/stack/kubernetes.png",
        ],
        buttons: [
            {text: t("projects.commonButtons.goToApp"),
                href: "https://kanban.jaimejcorral.com/about"
            }
        ]
    }

    const items = [kanbanItem]
    const [selected, setSelected] = useState(0)
    const touchStart = useRef<{ x: number, y: number } | null>(null)
    const move = (left?: boolean) => {
        if (left) {
            if (selected > 0)
                setSelected(selected - 1)
            else
                setSelected(items.length - 1)
        } else {
            if (selected < items.length - 1)
                setSelected(selected + 1)
            else
                setSelected(0)
        }
    }

    const onTouchStart = (e: TouchEvent) => {
        if (e.touches.length !== 1) {
            touchStart.current = null
            return
        }
        touchStart.current = {x: e.touches[0].clientX, y: e.touches[0].clientY}
    }

    const onTouchEnd = (e: TouchEvent) => {
        const start = touchStart.current
        touchStart.current = null
        if (!start)
            return
        const dx = e.changedTouches[0].clientX - start.x
        const dy = e.changedTouches[0].clientY - start.y
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5)
            move(dx > 0)
    }

    return (
        <div
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className={"relative flex min-h-[400px] w-full touch-pan-y flex-col overflow-hidden rounded-2xl border " +
                "border-borderLight dark:border-borderDark sm:min-h-[460px]"}>
            <CarrouselItem item={items[selected]}/>
            <div id={"menu"}
                 className={"absolute bottom-4 left-[50%] flex -translate-x-1/2 select-none items-center gap-3"}>
                <div aria-label={"Previous slide"}
                     className={"flex h-10 w-10 touch-manipulation cursor-pointer items-center justify-center " +
                         "rounded-full bg-slate-950/50 text-white backdrop-blur-sm transition-colors " +
                         "hover:bg-slate-950/70 active:bg-slate-950/70 sm:h-8 sm:w-8"}
                     onClick={() => move(true)}>
                    <FontAwesomeIcon size={"sm"} icon={faChevronLeft}/>
                </div>
                <div className={"flex items-center gap-2"}>
                    {items.map((x, i) =>
                        <button type={"button"} aria-label={`Slide ${i + 1}`} key={i}
                            className={`block h-2.5 cursor-pointer touch-manipulation rounded-full transition-all ${i === selected ?
                                "w-6 bg-white" : "w-2.5 bg-white/40 hover:bg-white/60 active:bg-white/60"}`}
                            onClick={() => setSelected(i)}/>)}
                </div>
                <div aria-label={"Next slide"}
                     className={"flex h-10 w-10 touch-manipulation cursor-pointer items-center justify-center " +
                         "rounded-full bg-slate-950/50 text-white backdrop-blur-sm transition-colors " +
                         "hover:bg-slate-950/70 active:bg-slate-950/70 sm:h-8 sm:w-8"}
                     onClick={() => move(false)}>
                    <FontAwesomeIcon size={"sm"} icon={faChevronRight}/>
                </div>
            </div>
        </div>
    )
}

export default Carrousel
